/**
 * Instalar o Brik como app (PWA), para a página /instalar.
 *
 * Chrome e Edge (Android e computador) avisam que o site é instalável pelo evento
 * `beforeinstallprompt`, e só uma vez, logo no carregamento — antes de qualquer tela montar. Por
 * isso ele é guardado aqui, desde o `main.tsx`, e a página só lê. iPhone não tem evento nenhum:
 * instalar é sempre pelo menu Compartilhar, e a página mostra o passo a passo.
 */

interface EventoInstalar extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let guardado: EventoInstalar | null = null;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach(f => f());

export const capturarInstalacao = () => {
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault(); // a página decide a hora, pelo botão
        guardado = e as EventoInstalar;
        avisar();
    });
    window.addEventListener('appinstalled', () => { guardado = null; avisar(); });
};

export const podeInstalarComUmToque = () => guardado !== null;

export const ouvirInstalacao = (f: () => void) => {
    ouvintes.add(f);
    return () => { ouvintes.delete(f); };
};

/** Abre a janela de instalação do navegador. Devolve se a pessoa aceitou. */
export const instalar = async (): Promise<boolean> => {
    if (!guardado) return false;
    const evento = guardado;
    guardado = null; // o evento só serve uma vez
    await evento.prompt();
    const { outcome } = await evento.userChoice;
    avisar();
    return outcome === 'accepted';
};

export type Plataforma = 'iphone' | 'android' | 'computador';

export const plataforma = (): Plataforma => {
    const ua = navigator.userAgent;
    if (/iphone|ipad|ipod/i.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)) return 'iphone';
    if (/android/i.test(ua)) return 'android';
    return 'computador';
};

/** Já está rodando como app instalado (aberto pelo ícone). */
export const rodandoInstalado = () =>
    window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
